import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { subscribeToBout, subscribeToDiscipline, subscribeToEvent } from "../firebase/services";
import logo from "../assets/logo.png";

const BLUE = "#64B5F6";
const RED = "#EF9A9A";

function judgeKey(name) {
  return name.replace(/[.#$/\[\]]/g, "_");
}

function submissionFor(bout, judgeName) {
  const submissions = bout.judgeSubmissions || {};
  return submissions[judgeKey(judgeName)]
    || Object.values(submissions).find((submission) => submission.judgeName === judgeName)
    || null;
}

function formatTimestamp(value) {
  if (!value) return null;
  const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
}

function roundLabel(index, originalTotalRounds) {
  const isTiebreaker = Number.isInteger(originalTotalRounds) && index >= originalTotalRounds;
  return `Round ${index + 1}${isTiebreaker ? " · Tiebreaker" : ""}`;
}

export default function AdminBoutDetail() {
  const { eventId, disciplineId, boutId } = useParams();
  const [bout, setBout] = useState(undefined);
  const [event, setEvent] = useState(null);
  const [discipline, setDiscipline] = useState(null);

  useEffect(() => subscribeToBout(eventId, boutId, setBout), [eventId, boutId]);
  useEffect(() => subscribeToEvent(eventId, setEvent), [eventId]);
  useEffect(() => {
    if (!disciplineId) return undefined;
    return subscribeToDiscipline(eventId, disciplineId, setDiscipline);
  }, [eventId, disciplineId]);

  // Seminar/training bouts have no discipline; they return to the event's bout list.
  const backPath = disciplineId
    ? `/event/${eventId}/discipline/${disciplineId}?tab=pools`
    : `/event/${eventId}?tab=disciplines`;

  const judges = useMemo(() => {
    if (!bout) return [];
    const assigned = bout.judges || [];
    const submittedNames = Object.values(bout.judgeSubmissions || {})
      .map((submission) => submission.judgeName)
      .filter(Boolean);
    return [...new Set([...assigned, ...submittedNames])];
  }, [bout]);

  if (bout === undefined) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading bout results…</div>;
  }

  if (!bout) {
    return (
      <div className="min-h-screen px-4 py-8 text-center">
        <p className="text-gray-400 mb-4">This bout could not be found.</p>
        <Link to={backPath} className="text-blue-300">{disciplineId ? "Back to discipline" : "Back to event"}</Link>
      </div>
    );
  }

  const confirmed = bout.confirmedResult;
  const totalRounds = Math.max(
    Number(bout.totalRounds) || 0,
    ...(bout.confirmedRounds || []).map((round) => (Number(round.roundIndex) || 0) + 1),
    ...judges.map((judge) => submissionFor(bout, judge)?.rounds?.length || 0),
  );
  const statusLabels = { waiting: "Waiting", scoring: "Scoring", submitted: "Awaiting confirmation", confirmed: "Confirmed" };

  return (
    <div className="min-h-screen px-3 py-4 sm:px-6 sm:py-6 lg:px-10">
      <div className="mx-auto max-w-6xl">
        <header className="mb-5">
          <img src={logo} alt="Joy of HEMA" className="w-64 sm:w-72 mx-auto" />
          <div className="text-center text-[10px] sm:text-xs tracking-[0.28em] text-gray-600 -mt-1 mb-5">STUDY · FENCE · GROW</div>
          <div>
            <Link
              to={backPath}
              className="px-3 py-2 bg-dark-700 border border-dark-500 rounded-lg text-sm text-gray-300 hover:text-white"
            >
              ← Back to {disciplineId ? discipline?.name || "Discipline" : event?.name || "Event"}
            </Link>
          </div>
        </header>

        <section className="bg-dark-700 border border-dark-500 rounded-2xl p-4 sm:p-6 mb-4">
          <div className="text-center mb-5">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-3 sm:gap-8 items-center">
              <div className="text-right">
                <div className="font-display text-xl sm:text-3xl font-extrabold" style={{ color: BLUE }}>{bout.fighterBlue}</div>
                <div className="text-[10px] uppercase tracking-wider text-gray-600">Blue</div>
              </div>
              <div className="font-display text-2xl sm:text-4xl font-extrabold text-white whitespace-nowrap">
                {confirmed ? `${confirmed.scoreB} – ${confirmed.scoreA}` : "vs"}
              </div>
              <div className="text-left">
                <div className="font-display text-xl sm:text-3xl font-extrabold" style={{ color: RED }}>{bout.fighterRed}</div>
                <div className="text-[10px] uppercase tracking-wider text-gray-600">Red</div>
              </div>
            </div>
            {confirmed?.winner && (
              <p className="mt-3 font-display font-bold text-green-400">
                {confirmed.winner === "Draw" ? "Draw" : `Winner: ${confirmed.winner}`}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            <Detail label="Event" value={event?.name || event?.title} />
            <Detail label="Discipline" value={bout.disciplineName || discipline?.name} />
            <Detail label="Pool / Stage" value={bout.poolName || bout.phase} />
            <Detail label="Bout" value={Number.isInteger(bout.order) ? `#${bout.order + 1}` : bout.round ? `Round ${bout.round}` : null} />
            <Detail label="Referee" value={bout.refName} />
            <Detail label="Ring" value={bout.ring || bout.location} />
            <Detail label="Rounds" value={totalRounds || bout.totalRounds} />
            <Detail label="Bout status" value={statusLabels[bout.status] || bout.status || "Unknown status"} />
          </div>
        </section>

        <section className="bg-dark-700 border border-dark-500 rounded-2xl overflow-hidden mb-4">
          <div className="px-4 sm:px-5 py-4 border-b border-dark-500">
            <h2 className="font-display text-lg font-bold">Official round totals</h2>
            <p className="text-xs text-gray-600 mt-1">Combined scores recorded when each round was confirmed.</p>
          </div>
          {totalRounds > 0 ? (
            <div className="divide-y divide-dark-500">
              {Array.from({ length: totalRounds }, (_, index) => {
                const round = (bout.confirmedRounds || []).find((item, position) => (item.roundIndex ?? position) === index);
                return (
                  <div key={index} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 px-4 sm:px-5 py-3">
                    <span className="font-display text-sm font-bold text-gray-400">{roundLabel(index, bout.originalTotalRounds)}</span>
                    <span className="font-display text-xl font-extrabold w-10 text-right" style={{ color: BLUE }}>{round?.blue ?? "—"}</span>
                    <span className="text-gray-700">–</span>
                    <span className="font-display text-xl font-extrabold w-10" style={{ color: RED }}>{round?.red ?? "—"}</span>
                  </div>
                );
              })}
            </div>
          ) : <p className="p-5 text-sm text-gray-600">No round results have been recorded yet.</p>}
        </section>

        {(bout.cardReopens || []).length > 0 && (
          <section className="bg-dark-700 border border-yellow-800 rounded-2xl p-4 sm:p-5 mb-4">
            <h2 className="font-display text-lg font-bold">Card corrections</h2>
            <p className="text-xs text-gray-600 mt-1 mb-3">Cards the referee reopened so a judge could fix a submitted score.</p>
            <ul className="text-sm text-gray-300 space-y-1.5">
              {[...bout.cardReopens].sort((a, b) => String(a.reopenedAt).localeCompare(String(b.reopenedAt))).map((entry, index) => (
                <li key={`${entry.judgeName}-${entry.reopenedAt}-${index}`}>
                  <span className="font-semibold">{entry.judgeName}</span>&apos;s card reopened by {entry.reopenedBy || "the referee"}
                  {formatTimestamp(entry.reopenedAt) && <span className="text-gray-500"> · {formatTimestamp(entry.reopenedAt)}</span>}
                  {entry.previousTotalRed != null && entry.previousTotalBlue != null && (
                    <span className="text-gray-500"> · previously{" "}
                      <span style={{ color: BLUE }}>{entry.previousTotalBlue}</span>–<span style={{ color: RED }}>{entry.previousTotalRed}</span>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        <section>
          <div className="mb-3">
            <h2 className="font-display text-xl font-bold">Judge scorecards</h2>
            <p className="text-xs text-gray-600 mt-1">Scores, live touch counts, penalties, and technical bonus decisions for each judge.</p>
          </div>
          {judges.length > 0 ? (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {judges.map((judge) => (
                <JudgeCard key={judge} judge={judge} bout={bout} />
              ))}
            </div>
          ) : (
            <div className="bg-dark-700 border border-dark-500 rounded-2xl p-5 text-sm text-gray-600">No judges are assigned to this bout.</div>
          )}
        </section>
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="bg-dark-800 rounded-lg p-3 min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-gray-600 mb-1">{label}</div>
      <div className="text-gray-300 truncate" title={value || "Not recorded"}>{value || "Not recorded"}</div>
    </div>
  );
}

function JudgeCard({ judge, bout }) {
  const submission = submissionFor(bout, judge);
  const rounds = submission?.rounds || [];
  const bonusFighter = submission?.boutWinner === "blue"
    ? bout.fighterBlue
    : submission?.boutWinner === "red" ? bout.fighterRed : null;
  const bonusColor = submission?.boutWinner === "blue" ? BLUE : RED;
  const submissionTime = formatTimestamp(submission?.submittedAt);

  return (
    <article className="bg-dark-700 border border-dark-500 rounded-2xl overflow-hidden">
      <div className="flex justify-between gap-3 px-4 sm:px-5 py-4 border-b border-dark-500">
        <div>
          <h3 className="font-display font-bold text-lg">{judge}</h3>
          {submissionTime && <p className="text-[10px] text-gray-600 mt-0.5">Submitted {submissionTime}</p>}
        </div>
        <span className={`text-xs font-bold ${submission?.complete ? "text-green-400" : submission ? "text-yellow-500" : "text-gray-600"}`}>
          {submission?.complete ? "Complete" : submission ? "In progress" : "Not submitted"}
        </span>
      </div>

      {rounds.length > 0 || submission ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <caption className="sr-only">Scores by round</caption>
            <thead className="text-[10px] uppercase tracking-wider text-gray-600 bg-dark-800">
              <tr>
                  <th scope="col" className="text-left px-4 py-2">Round</th>
                  <th scope="col" className="text-center px-2 py-2">Blue touches</th>
                  <th scope="col" className="text-center px-2 py-2">Red touches</th>
                  <th scope="col" className="text-center px-2 py-2">Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-500">
              {rounds.map((round, position) => {
                const index = round.index ?? position;
                return (
                  <tr key={index}>
                    <th scope="row" className="px-4 py-3 text-left font-display text-xs font-bold text-gray-400 whitespace-nowrap">
                      {roundLabel(index, bout.originalTotalRounds)}
                    </th>
                    <td className="px-2 py-3 text-center text-gray-400">{round.touchesBlue ?? 0}</td>
                    <td className="px-2 py-3 text-center text-gray-400">{round.touchesRed ?? 0}</td>
                    <td className="px-2 py-3 text-center whitespace-nowrap">
                      <span className="font-display text-lg font-extrabold" style={{ color: BLUE }}>{round.blue}</span>
                      <span className="text-gray-700 mx-2">–</span>
                      <span className="font-display text-lg font-extrabold" style={{ color: RED }}>{round.red}</span>
                      {(round.penaltyBlue || round.penaltyRed) ? (
                        <div className="text-[10px] text-yellow-700 mt-0.5">
                          Penalties: blue {round.penaltyBlue || 0}, red {round.penaltyRed || 0}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {submission && (
              <tfoot className="border-t border-dark-500 bg-dark-800/50">
                <tr>
                  <td className="px-4 py-4 text-xs uppercase tracking-wider text-gray-600">Judge card total</td>
                  <td />
                  <td />
                  <td className="px-2 py-4 text-center whitespace-nowrap font-display text-xl font-extrabold">
                    <span style={{ color: BLUE }}>{submission.totalBlue ?? "—"}</span>
                    <span className="text-gray-700 mx-2">–</span>
                    <span style={{ color: RED }}>{submission.totalRed ?? "—"}</span>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      ) : <p className="px-5 py-4 text-sm text-gray-600">No round scores submitted.</p>}

      {bonusFighter && (
        <div className="px-4 sm:px-5 py-4 border-t border-dark-500 bg-dark-800/50">
          <div className="p-3 rounded-lg border border-yellow-900/60 bg-yellow-950/20">
            <div className="text-xs font-bold text-yellow-600">Technical bonus: +1 to <span style={{ color: bonusColor }}>{bonusFighter}</span></div>
            <div className="text-xs text-gray-400 mt-1.5"><span className="text-gray-600">Reason:</span> {submission.boutWinnerComment || "No reason recorded."}</div>
          </div>
        </div>
      )}

      {submission?.confirmedDraw && (
        <div className="px-4 sm:px-5 py-4 border-t border-dark-500 bg-dark-800/50">
          <div className="p-3 rounded-lg border border-gray-700 bg-dark-900/40 text-xs font-bold text-gray-400">
            Judge confirmed this card as a draw — no technical bonus awarded.
          </div>
        </div>
      )}
    </article>
  );
}
